import { useState } from "react";
import { useSelectedCohort } from "@/hooks/useSelectedCohort";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Flame, Trophy, Crown, Medal, Zap, GraduationCap, Building2, User
} from "lucide-react";
import { useStreaks, useBadges, type ActiveStreak } from "@/hooks/useParticipantData";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BADGE_META, getBadgeMeta } from "@/lib/badgeMeta";

// Ranking query — filtra por el scope del usuario para evitar mezclar rachas
// de cohortes/scopes distintos y para no duplicar usuarios multi-cohorte.
function useStreakRanking(scope: Pick<ActiveStreak, 'scope_type' | 'scope_id'> | null) {
  return useQuery({
    queryKey: ['streak-ranking', scope?.scope_type, scope?.scope_id ?? null],
    queryFn: async () => {
      if (!scope || scope.scope_type !== 'cohort' || !scope.scope_id) return [];

      const { data, error } = await (supabase as any).rpc('get_cohort_streak_ranking', {
        _cohort_id: scope.scope_id,
        _limit: 10,
      });
      if (error) throw error;
      return ((data ?? []) as any[]).map((d: any, i: number) => ({
        userId: d.user_id as string,
        name: (d.full_name as string) || 'Participante',
        streak: (d.current_streak as number) ?? 0,
        rank: i + 1,
      }));
    },
    enabled: !!scope,
  });
}

function getScopeIcon(scope_type: ActiveStreak['scope_type']) {
  switch (scope_type) {
    case 'cohort': return GraduationCap;
    case 'organization': return Building2;
    case 'personal': return User;
  }
}

function StreakCard({ streak }: { streak: ActiveStreak }) {
  const ScopeIcon = getScopeIcon(streak.scope_type);
  return (
    <Card className="glass-card border-primary/20">
      <CardContent className="pt-6">
        <div className="text-center space-y-4">
          <div className="relative inline-flex">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center animate-pulse">
              <Flame className="w-10 h-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-background border-2 border-primary flex items-center justify-center">
              <span className="text-base font-bold text-primary">{streak.current_streak}</span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-center gap-1.5 text-muted-foreground text-xs mb-1">
              <ScopeIcon className="w-3.5 h-3.5" />
              <span className="truncate max-w-[220px]">{streak.scope_label}</span>
            </div>
            <p className="text-sm text-foreground">
              {streak.current_streak > 0
                ? `¡${streak.current_streak} días consecutivos!`
                : "Comienza tu racha hoy"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const GamificationSection = () => {
  const { data: streaks = [] } = useStreaks();
  const { data: badges = [] } = useBadges();
  const { activeEnrollment } = useSelectedCohort();

  // Rachas de cohorte (rutas con acompañamiento) vs racha personal
  // (autoaprendizaje o sin ruta). Solo las de cohorte tienen ranking.
  const cohortStreaks = streaks.filter(s => s.scope_type === 'cohort');
  const personalStreak = streaks.find(s => s.scope_type !== 'cohort');

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const activeCohortKey =
    activeEnrollment && !activeEnrollment.is_perpetual &&
    cohortStreaks.some(s => s.scope_id === activeEnrollment.cohort_id)
      ? activeEnrollment.cohort_id
      : null;
  const defaultKey =
    activeCohortKey ??
    (activeEnrollment?.is_perpetual && personalStreak ? 'personal' : null) ??
    cohortStreaks[0]?.scope_id ??
    (personalStreak ? 'personal' : null);
  const activeKey = selectedKey ?? defaultKey;

  const selectedStreak =
    activeKey === 'personal'
      ? personalStreak
      : cohortStreaks.find(s => s.scope_id === activeKey) ?? cohortStreaks[0] ?? personalStreak;

  const isCohortScope = selectedStreak?.scope_type === 'cohort';
  const rankingScope = selectedStreak && isCohortScope
    ? { scope_type: selectedStreak.scope_type, scope_id: selectedStreak.scope_id }
    : null;
  const { data: ranking = [] } = useStreakRanking(rankingScope);

  const currentValue = selectedStreak?.current_streak ?? 0;
  const bestValue = selectedStreak?.max_streak ?? 0;
  const goal = Math.max(bestValue + 1, 30);

  const hasSelector = cohortStreaks.length + (personalStreak ? 1 : 0) > 1;

  const earnedBadgeKeys = new Set(badges.map(b => b.badge_key));

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return <Crown className="w-5 h-5 text-amber-400" />;
      case 2: return <Medal className="w-5 h-5 text-slate-400" />;
      case 3: return <Medal className="w-5 h-5 text-amber-600" />;
      default: return <span className="text-sm font-bold text-muted-foreground">#{rank}</span>;
    }
  };

  return (
    <div className="space-y-6">
      {hasSelector && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground shrink-0">Ver racha de:</span>
          <Select value={activeKey ?? undefined} onValueChange={setSelectedKey}>
            <SelectTrigger className="w-full sm:w-[320px]">
              <SelectValue placeholder="Selecciona" />
            </SelectTrigger>
            <SelectContent>
              {cohortStreaks.map(s => (
                <SelectItem key={s.scope_id ?? 'c'} value={s.scope_id as string}>
                  Ruta: {s.scope_label}
                </SelectItem>
              ))}
              {personalStreak && (
                <SelectItem value="personal">Personal (autoaprendizaje)</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Racha del scope seleccionado + Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          {!selectedStreak ? (
            <Card className="glass-card border-primary/20">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 mx-auto rounded-full bg-muted flex items-center justify-center">
                    <Flame className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Comienza tu racha</h3>
                    <p className="text-sm text-muted-foreground">
                      Ingresa cada día para acumular racha
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <StreakCard streak={selectedStreak} />
          )}
        </div>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-amber-400" />
              Estadísticas de Racha
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Tu mejor racha</span>
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="font-bold text-foreground">{bestValue} días</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {isCohortScope ? 'Progreso hacia 30 días' : `Progreso hacia superar tu récord (${goal} días)`}
                </span>
                <span className="text-primary">{Math.min(100, Math.round((currentValue / goal) * 100))}%</span>
              </div>
              <Progress value={Math.min(100, (currentValue / goal) * 100)} className="h-2" />
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              {isCohortScope
                ? 'En rutas con acompañamiento tu racha se compara con los participantes de tu cohorte. Cada cohorte tiene su propia racha.'
                : 'En rutas de autoaprendizaje tu racha es personal: no se compara con otros participantes. Tu reto es superar tu mejor racha.'}
            </p>
          </CardContent>
        </Card>
      </div>


      {/* Achievements */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Logros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Show known badges (earned + locked) */}
            {Object.entries(BADGE_META).map(([key, meta]) => {
              const unlocked = earnedBadgeKeys.has(key);
              const IconComponent = meta.icon;
              return (
                <div
                  key={key}
                  className={`p-4 rounded-xl border text-center transition-all ${
                    unlocked
                      ? "bg-primary/10 border-primary/30"
                      : "bg-muted/30 border-border/50 opacity-60"
                  }`}
                >
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${
                    unlocked ? "bg-primary/20" : "bg-muted"
                  }`}>
                    <IconComponent className={`w-6 h-6 ${
                      unlocked ? "text-primary" : "text-muted-foreground"
                    }`} />
                  </div>
                  <h4 className="font-medium text-foreground">{meta.name}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{meta.description}</p>
                </div>
              );
            })}
            {/* Show earned badges not in BADGE_META */}
            {badges
              .filter(b => !BADGE_META[b.badge_key])
              .map(b => (
                <div
                  key={b.badge_key}
                  className="p-4 rounded-xl border text-center transition-all bg-primary/10 border-primary/30"
                >
                  <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 bg-primary/20">
                    <Trophy className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="font-medium text-foreground">{getBadgeMeta(b.badge_key).name}</h4>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Ranking — solo en rutas con acompañamiento (cohorte) */}
      {isCohortScope && (
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            Ranking de Rachas
          </CardTitle>
          <p className="text-xs text-muted-foreground pt-1">
            {`Te comparas con los participantes de tu cohorte "${selectedStreak?.scope_label}"`}
          </p>
        </CardHeader>

        <CardContent>
          {ranking.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No hay datos de ranking aún</p>
          ) : (
            <div className="space-y-3">
              {ranking.map((entry) => (
                <div
                  key={entry.userId}
                  className="flex items-center gap-4 p-3 rounded-lg bg-background/50 border border-border/50"
                >
                  <div className="w-8 flex justify-center">
                    {getRankIcon(entry.rank)}
                  </div>
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {entry.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{entry.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-orange-500" />
                    <span className="font-bold text-foreground">{entry.streak}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>

  );
};

export default GamificationSection;
